// The draft rows a multi-cell set, clear or paste stages (#589).
//
// ⚠️ THERE IS ONE WRITE PATH, and this feeds it. Every row here goes through
// `POST /manpower/schedule/draft/entries/bulk` — the endpoint the crew stepper
// and ⇄ Shift already use — so a mass edit is a draft like any other: invisible
// to PMs until Publish, discarded by Reset (which is the undo; nothing here
// builds a second one), and behind the same guards (manpower.edit /
// manpower.edit.fab, ProjectStageGuard reading every `entries[]` project, the
// #411 carve-out, and the service's maintenance-parent and fab-scope checks,
// any of which refuses the whole batch).
//
// What this module adds is only what the single-cell and stepper paths already
// do per cell, done for many at once:
//
//  1. THE FAB-LINE MIGRATION. A fab job's Fabrication line displays legacy
//     NULL-phase entries as a fallback; writing phase='fabrication' over one
//     without zeroing it would double-count the day. Same as
//     `commitEdit` and `stepRow` in ManpowerPage.
//
//  2. THE PIN. A day whose crew differs from its line's normal rate is pinned
//     (isOverride — the orange outline the stepper steps around). Typing
//     compares against the rate BEFORE the edit, which for one cell is the same
//     thing as after. For a block it is not: set a whole run from 5 to 6 and a
//     before-rate of 5 would pin every day, leaving the stepper nothing to
//     step. So the normal rate here is the one set by the days the batch DIDN'T
//     touch — and when it touched them all, by the value it wrote. Set next
//     week to 8 on a line of 5s and next week is pinned; set the whole run to
//     6 and nothing is.
//
//  3. NO-OPS ARE DROPPED, so a paste of the same numbers back stages nothing
//     (the server's cleanup would delete them anyway; this keeps the request
//     and the "N changed" count honest).
//
//     ⚠️ "Same number" is the whole test — a cell whose crew doesn't change
//     KEEPS ITS PIN STATE, pinned or not. Re-deriving the pin for it turned a
//     copy-paste of an uneven, unpinned row (as the Excel import leaves them —
//     it writes no isOverride) into a batch of pins on every off-rate day:
//     nothing changed on screen, the report said 0 cells, and the stepper
//     then skipped those days for good (Codex on #612). Such a cell also
//     counts as LEFT ALONE for rule 2's normal rate. Two things still write an
//     unchanged figure, both carrying the existing pin across: the fab-line
//     migration (rule 1), and a figure that is itself a STAGED draft, which
//     may be another scheduler's — re-staging it puts it in this scheduler's
//     draft, as typing it would (see planBulkWrites).

import type { CrewRow } from './scheduleModel';
import {
  baseRate, getPhaseCellMeta, nullPhaseEntryFor, stepperTargetDays, stepperValue,
} from './scheduleModel';
import type { DraftEntryInput } from '../hooks/useDraftEdits';

/**
 * The bulk draft route's limit — `@ArrayMaxSize(1500)` on BulkDraftEntriesDto.
 * Checked before sending so the scheduler gets a straight answer rather than a
 * 400 from a request we could see was over.
 */
export const BULK_DRAFT_MAX_ENTRIES = 1500;

export interface BulkPlan {
  entries: DraftEntryInput[];
  /** Cells whose crew actually changes. */
  changed: number;
  /** Of those, cells that already had crew and now have a different figure. */
  replaced: number;
  /**
   * Cells staged although their figure doesn't change: a fab day moved off its
   * legacy NULL-phase row (rule 1), or a cell whose shown figure is a STAGED
   * draft, re-staged so it is in this scheduler's draft (rule 3). Reported, so
   * a batch that stages only these doesn't read as "0 cells".
   */
  restaged: number;
}

/**
 * Plan the draft rows for a set of writes that have ALREADY been through
 * `partitionWrites` (cellSelection.ts) — every write here is one the scheduler
 * may make.
 *
 * @param windowDays every day in the grid's window, as the stepper reads it.
 */
export function planBulkWrites(
  writes: readonly { row: CrewRow; ds: string; value: number }[],
  windowDays: string[],
): BulkPlan {
  // Last write to a cell wins, grouped by crew line.
  const byRow = new Map<string, { row: CrewRow; cells: Map<string, number> }>();
  for (const w of writes) {
    const g = byRow.get(w.row.key) ?? { row: w.row, cells: new Map<string, number>() };
    g.cells.set(w.ds, w.value);
    byRow.set(w.row.key, g);
  }

  const entries: DraftEntryInput[] = [];
  let changed = 0;
  let replaced = 0;
  let restaged = 0;
  for (const { row, cells } of byRow.values()) {
    // Rule 2: the rate of the days this batch leaves alone — including cells
    // it "writes" with the figure they already hold (rule 3).
    const unchangedHere = (ds: string) => cells.get(ds) === getPhaseCellMeta(row, ds).count;
    const untouched = stepperTargetDays(row, windowDays).filter((ds) => !cells.has(ds) || unchangedHere(ds));
    const base = stepperValue(row, untouched) || baseRate([...cells.values()]);

    for (const [ds, value] of [...cells.entries()].sort(([a], [b]) => a.localeCompare(b))) {
      const meta = getPhaseCellMeta(row, ds);
      const legacy = row.phase === 'fabrication' ? nullPhaseEntryFor(row.project, ds) : undefined;
      const unchanged = meta.count === value;
      // Rule 3: an unchanged figure is not written, and never re-pinned —
      // UNLESS it is a staged draft. The overlay shows every scheduler's
      // drafts and a draft cell has one owner (the last writer), so skipping
      // it would leave a figure this scheduler just "set" in someone else's
      // draft: outside their own Publish, and undone by the other person's
      // Reset. Re-staging it claims it, exactly as typing the figure would.
      if (unchanged && !legacy && !meta.isDraft) continue;
      const isOverride = unchanged ? meta.isOverride : value !== base;
      if (unchanged) {
        restaged++;
      } else {
        changed++;
        if (meta.count > 0) replaced++;
      }
      entries.push({ workDate: ds, projectId: row.projectId, crewCount: value, phase: row.phase, isOverride });
      // Rule 1.
      if (legacy) entries.push({ workDate: ds, projectId: row.projectId, crewCount: 0, phase: null, isOverride: false });
    }
  }
  return { entries, changed, replaced, restaged };
}

/** The booked crew on each cell of a block, for Ctrl+C. Bid-forecast figures
 *  are NOT copied: they are derived from the bid, not booked, and pasting one
 *  would book it as though someone had typed it. */
export function copyBlock(rows: readonly CrewRow[], days: readonly string[]): number[][] {
  return rows.map((row) => days.map((ds) => (row.project.isMaintenance ? 0 : getPhaseCellMeta(row, ds).count)));
}

/**
 * The draft rows for ONE typed cell — what `commitEdit` stages, kept here so
 * the rules are assertable (the web tests run without a DOM).
 *
 * - Typed: the day is pinned when it differs from the line's normal rate, and
 *   typing that rate back un-pins it.
 * - Untouched (opened and left without a keystroke): nothing is re-derived —
 *   the cell keeps its pin state, and a figure that is already what the grid
 *   shows stages nothing (rule 3, as for a pasted cell). Without this, merely
 *   clicking an off-rate day of an imported row pinned it, and clicking an
 *   empty cell staged a pinned zero.
 * - A fab day still on a legacy NULL-phase row also zeroes it, in the SAME
 *   batch (rule 1), so the day can't end up with one half of the migration.
 */
export function planTypedCell(
  row: CrewRow, ds: string, value: number, windowDays: string[], untouched: boolean,
): DraftEntryInput[] {
  const meta = getPhaseCellMeta(row, ds);
  const legacy = row.phase === 'fabrication' ? nullPhaseEntryFor(row.project, ds) : undefined;
  if (untouched && meta.count === value && !legacy && !meta.isDraft) return [];
  const base = stepperValue(row, stepperTargetDays(row, windowDays));
  const isOverride = untouched ? meta.isOverride : value !== base;
  const entries: DraftEntryInput[] = [{ workDate: ds, projectId: row.projectId, crewCount: value, phase: row.phase, isOverride }];
  if (legacy) entries.push({ workDate: ds, projectId: row.projectId, crewCount: 0, phase: null, isOverride: false });
  return entries;
}
